import { Component, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, Subscription, switchMap, throwError } from 'rxjs';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';
import { DataService } from 'src/app/core/services/data.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { ManagedServicesRepository } from '../../services/managed-services-repository.service';
import { MspTicketDetailService } from '../../services/ticket-detail.service';
import { IMspParentChildTicket } from '../interfaces/msp-parent-child-ticket';
import { IMspStatusData } from '../interfaces/msp-statuses-map';
import { MspTicket } from '../interfaces/msp-ticket';
import { ICwTicketAttachment } from '../interfaces/msp-ticket-attachment';
import { ICwTicketNoteWithAttachmentCount } from '../interfaces/msp-ticket-note-attachment-count';
import { IMspTicketNoteResponse } from '../interfaces/msp-ticket-note-response';
import { IMspTicketApprovalWithEntriesAndApprovers } from '../models/msp-ticket-approval-with-entry-and-approvers';
import { getStatusData } from '../utils/tickets-table-utils';
import { CloseTicketDialogComponent } from './components/close-ticket-dialog/close-ticket-dialog.component';

@Component({
  selector: 'app-ticket-detail',
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.scss'],
  standalone: false,
})
export class TicketDetailComponent implements OnInit {
  canCloseTicket: boolean = false;

  isSidenavClosed: boolean = false;

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private dataService: DataService,
    private activatedRoute: ActivatedRoute,
    private userDataService: UserDataService,
    private permissionsService: PermissionsService,
    public managedServiceRepository: ManagedServicesRepository,
    public mspTicketDetailService: MspTicketDetailService,
  ) {
    this.userDataService.user$.subscribe(() => {
      this.canCloseTicket = this.permissionsService.hasPermission('managed-services', 'Tickets', 'close');
    });
  }

  private subscriptions = new Subscription();

  /** Hold ticket id from the URL */
  ticketNumber: string = '';

  /** Indicates if the ticket was recently created */
  isNewlyCreated: boolean = false;

  /** Indicates if the ticket was modified in this view (e.g. closed), so the list needs a refresh on back */
  private hasTicketBeenModified: boolean = false;

  /** Hold loading state for ticket detail and header */
  isLoadingData: boolean = true;

  /** Hold ticket details */
  ticketDetails: MspTicket;

  /** Map to hold MSP statuses */
  statusMap: IMspStatusData[];

  /** Hold ticket notes */
  ticketNotes: ICwTicketNoteWithAttachmentCount[] = [];

  /** Hold loading state for notes and attachments */
  isLoadingNotes: boolean = true;

  isLoadingAttachments: boolean = true;

  /** Hold the dialog reference for the close ticket dialog */
  dialogRef: MatDialogRef<CloseTicketDialogComponent, any>;

  /** Map to hold color values for each priority */
  priorityColorMap = {
    'Priority 1': 'var(--error-red)',
    'Priority 2': 'var(--udt-orange)',
    'Priority 3': 'var(--udt-yellow)',
    'Priority 4': 'var(--udt-blue)',
    'Priority 5': 'var(--udt-green)',
  };

  /** Map to hold abbreviation for each priority */
  priorityAbbreviationMap = {
    'Priority 1': 'P1',
    'Priority 2': 'P2',
    'Priority 3': 'P3',
    'Priority 4': 'P4',
    'Priority 5': 'P5',
  };

  /** Flag to hold the state of the sidenav */
  isSideNavCollapsed: boolean = false;

  /** Flag to indicate that the ticket is already resolved (closed)
   * Useful to show/hide the close ticket button
   */
  isTicketClosed: boolean = true;

  /**
   * Hold the ticket approvals data
   */
  approvals: IMspTicketApprovalWithEntriesAndApprovers | null = null;

  /**
   * Flag to indicate if the ticket is approved or denied externally
   */
  isApprovedOrDeniedExternally: boolean = false;

  /** Cw Approved ticket status */
  cwApprovedStatus: string = 'w: Approval: Approved (Client)';

  /** Cw Denied ticket status */
  cwDeniedStatus: string = 'w: Approval: Denied (Client)';

  /** Hold parent ticket data if this is a child ticket */
  parentTicketData: IMspParentChildTicket | null = null;

  /** Hold loading state for parent ticket Card*/
  isLoadingParentTicket: boolean = true;

  /** Hold loading state for child tickets */
  isLoadingChildTickets: boolean = true;

  /** Hold the approval module ID */
  approvalModuleId: string = 'change-management';

  /** Hold child tickets */
  childTickets: IMspParentChildTicket[] = [];

  /** Flags to track API failures */
  apiTicketDetailFailed: boolean = false;
  apiStatusMapFailed: boolean = false;
  apiTicketNotesFailed: boolean = false;
  apiTicketAttachmentsFailed: boolean = false;
  apiTicketApprovalsFailed: boolean = false;
  apiParentTicketFailed: boolean = false;
  apiChildTicketsFailed: boolean = false;

  /**
   * Get the ticket number from the URL and fetch the ticket details
   */
  ngOnInit(): void {
    const ticketNumberSub = this.activatedRoute.paramMap.subscribe((params) => {
      this.ticketNumber = params.get('ticketNumber');
      this.mspTicketDetailService.resetTicketDetails();

      // Check for the 'newlyCreated' query parameter
      this.activatedRoute.queryParams.subscribe((queryParams) => {
        this.isNewlyCreated = queryParams['newlyCreated'] === 'true';
      });

      this.loadInitialData();
    });
    this.subscriptions.add(ticketNumberSub);
    this.mspTicketDetailService.ticketDetails.subscribe({
      next: (ticket) => {
        this.ticketDetails = ticket;
      },
    });
    this.mspTicketDetailService.ticketNotes.subscribe({
      next: (notes) => {
        this.ticketNotes = notes;
      },
    });
    this.mspTicketDetailService.isLoadingAttachments.subscribe({
      next: (isLoading) => {
        this.isLoadingAttachments = isLoading;
      },
    });
    this.mspTicketDetailService.isLoadingNotes.subscribe({
      next: (isLoading) => {
        this.isLoadingNotes = isLoading;
      },
    });
    this.dataService.isCollapsedSidenav$.subscribe({
      next: (data) => {
        this.isSideNavCollapsed = data;
      },
    });

    this.dataService.isExpandedSidenavClosed$.subscribe({
      next: (isClosed) => {
        this.isSidenavClosed = isClosed;
      },
    });
  }

  /**
   * Update the isApprovedOrDeniedExternally flag when the ticket approval changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['ticketDetails'] || changes['approvals']) {
      this.updateIsApprovedOrDeniedExternally();
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  ngOnDestroy(): void {
    this.mspTicketDetailService.resetTicketDetails();
    this.subscriptions.unsubscribe();
  }

  /**
   * Load ticket details, statuses map, and after that load ticket notes and attachments
   */
  private loadInitialData(): void {
    this.isLoadingData = true;

    // Reset API failure flags
    this.apiTicketDetailFailed = false;
    this.apiStatusMapFailed = false;
    this.apiTicketApprovalsFailed = false;
    this.apiTicketNotesFailed = false;
    this.apiTicketAttachmentsFailed = false;
    this.apiParentTicketFailed = false;
    this.apiChildTicketsFailed = false;

    const ticketDetail$ = this.managedServiceRepository.getTicketDetail(Number(this.ticketNumber)).pipe(
      catchError((error) => {
        this.apiTicketDetailFailed = true;
        const errorMessage = error['error']?.['error']?.['errorMessage'];
        if (error.status === 404 && errorMessage === 'Ticket not found') {
          this.openSnackBar(
            `Ticket ${this.ticketNumber} is no longer available. If you believe this is a mistake, please contact UDT Support for assistance.`,
            'error',
            0,
          );
        } else {
          this.openSnackBar('Error loading ticket details', 'error');
        }
        this.goBackWithRefresh();
        return throwError(() => error);
      }),
    );
    const statusesMap$ = this.managedServiceRepository.getStatusesMap().pipe(
      catchError((error) => {
        this.apiStatusMapFailed = true;
        this.openSnackBar('Error loading status information', 'error');
        this.goBackWithRefresh();
        return throwError(() => error);
      }),
    );
    const approvals$ = this.managedServiceRepository
      .getTicketApproval(Number(this.ticketNumber), this.approvalModuleId)
      .pipe(
        catchError((error) => {
          this.apiTicketApprovalsFailed = true;
          return of(null);
        }),
      );
    const initialDataSubscription = forkJoin([ticketDetail$, statusesMap$, approvals$])
      .pipe(
        map(([ticket, statusMap, approvals]) => {
          this.mspTicketDetailService.updateTicketDetails(ticket);
          this.isTicketClosed =
            getStatusData(ticket.statusName, ticket.boardName, statusMap)?.isClosed === true ||
            ticket.statusName === 'Closed';
          this.mspTicketDetailService.updateTicketDetails({ ...ticket, isClosed: this.isTicketClosed });
          this.statusMap = statusMap;
          this.approvals = approvals;
          this.updateIsApprovedOrDeniedExternally();
          this.isLoadingData = false;

          // Create separate observables with explicit types
          const notesObservable: Observable<IMspTicketNoteResponse> = this.managedServiceRepository
            .getTicketNotes(Number(this.ticketNumber), this.mspTicketDetailService?.ticketNoteFilters)
            .pipe(
              catchError((error) => {
                this.apiTicketNotesFailed = true;
                this.openSnackBar('Error loading ticket notes', 'error');
                return of({ notes: [], count: 0 } as IMspTicketNoteResponse);
              }),
            );

          const attachmentsObservable: Observable<ICwTicketAttachment[]> = this.managedServiceRepository
            .getTicketAttachments(Number(this.ticketNumber))
            .pipe(
              catchError((error) => {
                this.apiTicketAttachmentsFailed = true;
                this.openSnackBar('Error loading ticket attachments', 'error');
                return of([]);
              }),
            );

          let parentTicketObservable: Observable<IMspParentChildTicket | null> = of(null);
          if (ticket.parentTicketId) {
            parentTicketObservable = this.managedServiceRepository.getTicketParentDetail(ticket.parentTicketId).pipe(
              catchError((error) => {
                this.apiParentTicketFailed = true;
                this.isLoadingParentTicket = false;
                return of(null);
              }),
            );
            this.isLoadingParentTicket = true;
          }

          let childTicketsObservable: Observable<IMspParentChildTicket[]> = of([]);
          if (ticket.childTicketCount > 0) {
            childTicketsObservable = this.managedServiceRepository.getTicketChildren(Number(this.ticketNumber)).pipe(
              catchError((error) => {
                this.apiChildTicketsFailed = true;
                this.isLoadingChildTickets = false;
                return of([]);
              }),
            );
            this.isLoadingChildTickets = true;
          }

          return { notesObservable, attachmentsObservable, parentTicketObservable, childTicketsObservable };
        }),
      )
      .subscribe({
        next: ({ notesObservable, attachmentsObservable, parentTicketObservable, childTicketsObservable }) => {
          // Wait for each observable in parallel with other different than forkJoin
          notesObservable.subscribe((notesResponse) => {
            this.loadNotes(notesResponse);
            this.isLoadingNotes = false;
          });

          // Wait for attachments
          attachmentsObservable.subscribe((attachments) => {
            this.loadAttachments(attachments);
          });

          // Parent Ticket
          parentTicketObservable.subscribe((parentTicket) => {
            if (parentTicket) {
              this.parentTicketData = parentTicket;
              this.isLoadingParentTicket = false;
            }
          });

          // Child Tickets
          childTicketsObservable.subscribe((childTickets) => {
            if (childTickets && childTickets.length > 0) {
              this.childTickets = childTickets;
              this.isLoadingChildTickets = false;
            }
          });
        },
      });

    this.subscriptions.add(initialDataSubscription);
  }

  /**
   * Go back to the tickets page with refresh
   */
  goBackWithRefresh() {
    const timestamp = new Date().getTime();

    this.router.navigateByUrl(`/secured/managed-services/tickets?refresh=${timestamp}`, { replaceUrl: true });
  }

  /**
   * Go back to the tickets page without refresh
   * If the ticket was recently created, it will use goBackWithRefresh instead
   */
  goBack() {
    if (this.isNewlyCreated || this.hasTicketBeenModified) {
      this.goBackWithRefresh();
    } else {
      this.router.navigateByUrl('/secured/managed-services/tickets', { replaceUrl: true });
    }
  }

  /**
   * Open Confirm Dialog to close the ticket
   */
  openCloseTicket() {
    this.dialogRef = this.matDialog.open(CloseTicketDialogComponent, {
      maxWidth: '816px',
      data: {
        confirmAction: (resolutionText: string) => {
          this.onCloseTicketConfirm(resolutionText);
        },
      },
      disableClose: true,
    });
  }

  /**
   * Update ticket status to closed
   */
  onCloseTicketConfirm(resolutionText: string) {
    this.dialogRef.componentInstance.data.isLoadingConfirm = true;
    this.managedServiceRepository
      .closeTicket(Number(this.ticketNumber), this.getClosedStatusByBoard(), resolutionText)
      .subscribe({
        next: () => {
          this.hasTicketBeenModified = true;
          this.loadInitialData();
          this.openSnackBar('Ticket closed successfully', 'success');
          this.dialogRef.close();
          this.dialogRef.componentInstance.data.isLoadingConfirm = false;
        },
        error: (error) => {
          this.openSnackBar('Error closing ticket', 'error');
          this.dialogRef.componentInstance.data.isLoadingConfirm = false;
        },
      });
  }

  /**
   * Executed when the approval status is updated
   */
  updateApproval(approval: IMspTicketApprovalWithEntriesAndApprovers): void {
    this.approvals = approval;
    if (approval.ticketApproval.status === 'approved' || approval.ticketApproval.status === 'denied') {
      this.loadInitialData();
    } else {
      this.managedServiceRepository
        .getTicketNotes(Number(this.ticketNumber), this.mspTicketDetailService?.ticketNoteFilters)
        .subscribe({
          next: (notesResponse) => this.loadNotes(notesResponse),
        });
    }
  }

  /**
   * Conditions to show the approvals card
   */
  showApprovals(): boolean {
    return (
      (!this.isLoadingData &&
        !(this.ticketDetails?.isClosed === true && this.approvals?.ticketApproval?.status === 'in-progress') &&
        (this.approvals !== null || this.isApprovedOrDeniedExternally)) ||
      this.apiTicketApprovalsFailed
    );
  }

  /**
   * Conditions to show the parent ticket card
   */
  showParentTicketCard(): boolean {
    return !this.isLoadingData && this.ticketDetails?.parentTicketId != null;
  }

  /**
   * Conditions to show the child tickets card
   */
  showChildTicketsCard(): boolean {
    return !this.isLoadingData && this.ticketDetails?.childTicketCount > 0;
  }

  /** Mark as approved or denied externally if the ticket approval is in progress but the ticket detail status is approved or denied */
  updateIsApprovedOrDeniedExternally() {
    this.isApprovedOrDeniedExternally =
      (this.approvals?.ticketApproval?.status === 'in-progress' || this.approvals === null) &&
      (this.ticketDetails?.statusName === this.cwApprovedStatus ||
        this.ticketDetails?.statusName === this.cwDeniedStatus);
  }

  /**
   * Return the closed status of the ticket based on the board name
   */
  private getClosedStatusByBoard(): string {
    const closedStatus = this.statusMap.find(
      (status) => status.boardName === this.ticketDetails.boardName && status?.isCloseDefault,
    )?.cwStatus;
    return closedStatus ?? 'res: Closed'; // Default should not be used
  }

  /**
   * Load ticket notes
   */
  private loadNotes(notesResponse: IMspTicketNoteResponse): void {
    this.mspTicketDetailService.updateTicketNotes(notesResponse?.notes);
    this.mspTicketDetailService.updateTicketNotesCount(notesResponse?.count);
  }

  /**
   * Load ticket notes and attachments
   */
  private loadAttachments(attachments: ICwTicketAttachment[]): void {
    //Clear attachments before processing incoming attachments
    this.mspTicketDetailService.updateTicketAttachments([]);
    const newAttachments = [...attachments];
    const attachmentPromises = newAttachments.map((attachment) =>
      this.mspTicketDetailService.processAttachment(attachment, true),
    );
    Promise.all(attachmentPromises).finally(() => {
      this.isLoadingAttachments = false;
    });
  }

  /**
   * Open a snackbar
   * */
  private openSnackBar(message: string, type: 'success' | 'error', duration: number = 5000) {
    this.snackBar.openFromComponent(SnackbarComponent, {
      data: {
        message,
        type,
        closeButton: true,
      },
      duration: duration,
    });
  }

  /**
   * Fetch email notifications for the current ticket
   */
  fetchEmailNotifications() {
    //Placeholder function, we use the automaticEmailCc from the ticket details in managed services.
    //On the other hand, in RPS tickets, email notifications are fetched from the API.
  }
}
